/* eslint-disable react/prop-types */
/**
 * /settings — Phase 8C landing/router page.
 *
 * Renders a tile grid linking to each pillar's settings sub-page.
 * Also serves as the redirector for legacy `/settings#<section>` deep
 * links — see `pages/settings/legacy_redirect.js` for the mapping table.
 *
 * Wrapped in <EntityShell> per the Phase 7 no-tabs pattern.
 */
import { Link } from "react-router-dom";
import {
  UserCog, Shield, TrendingUp, ClipboardList, Users, Boxes,
  Briefcase, Receipt, Server,
} from "lucide-react";
import { EntityShell } from "../../components/shell";
import { useAuth } from "../../context/AuthContext";
import LegacyHashRedirect from "./legacy_redirect";

const TILES = [
  { to: "profile",    label: "Profile",    icon: UserCog,        accent: "#1FB6C1", desc: "Avatar, password, sessions, install, AI prefs", roles: "all" },
  { to: "team",       label: "Team",       icon: Users,          accent: "#A78BFA", desc: "Users, roles, password reset requests",        roles: "manager" },
  { to: "sales",      label: "Sales",      icon: TrendingUp,     accent: "#10B981", desc: "Lead stages, quote templates",                 roles: "manager" },
  { to: "service",    label: "Service",    icon: ClipboardList,  accent: "#F59E0B", desc: "Job-card workflow, ticket SLAs",               roles: "manager" },
  { to: "operations", label: "Operations", icon: Boxes,          accent: "#06B6D4", desc: "Fleet vehicles, phone normalisation",          roles: "manager" },
  { to: "projects",   label: "Projects",   icon: Briefcase,      accent: "#3B82F6", desc: "Phase definitions, milestone defaults",        roles: "manager" },
  { to: "finance",    label: "Finance",    icon: Receipt,        accent: "#F43F5E", desc: "Invoice numbering, tax codes",                 roles: "manager" },
  { to: "platform",   label: "Platform",   icon: Server,         accent: "#F59E0B", desc: "Integrations, taxonomy, migration, diagnostics", roles: "admin" },
];

function isVisible(roleSpec, userRole) {
  if (roleSpec === "all") return true;
  if (roleSpec === "manager")
    return userRole === "manager" || userRole === "admin" || userRole === "owner";
  if (roleSpec === "admin") return userRole === "admin" || userRole === "owner";
  return false;
}

export default function SettingsIndex() {
  const { user } = useAuth();
  const tiles = TILES.filter((t) => isVisible(t.roles, user?.role));

  return (
    <EntityShell
      kindLabel="Settings"
      title="Settings"
      backTo="/"
    >
      <LegacyHashRedirect />
      <div className="space-y-6" data-testid="settings-index-page">
        <p className="text-sm text-[#a0aabf] max-w-3xl">
          Choose a pillar to configure.  Profile lives at the top; the rest
          are organised by domain so the right person can find the right
          setting fast.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tiles.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`settings-tile-${t.to}`}
              className="hv-card p-4 hover:border-white/20 transition group"
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-md border flex items-center justify-center shrink-0"
                  style={{
                    backgroundColor: `${t.accent}20`,
                    borderColor: `${t.accent}60`,
                  }}
                >
                  <t.icon className="w-5 h-5" style={{ color: t.accent }} />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold group-hover:text-white">
                    {t.label}
                  </div>
                  <div className="text-xs text-[#a0aabf] mt-1 leading-relaxed">
                    {t.desc}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </EntityShell>
  );
}
